import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import {
  CheckCircle2,
  CircleAlert,
  ClipboardCheck,
  CreditCard,
  History,
  KeyRound,
  MonitorSmartphone,
  RotateCcw,
  Timer,
  TriangleAlert,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { api, type Pagina } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { CampoDeBusqueda } from '@/shared/ui/CampoDeBusqueda';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { Modal, Campo, claseEntrada } from '@/shared/ui/Modal';
import { Tabla, type Columna } from '@/shared/ui/Tabla';
import { notificar } from '@/shared/ui/avisos';
import { useValorDiferido } from '@/shared/ui/useValorDiferido';
import { useSesion } from '@/shared/sesion/SesionProvider';
import type {
  CodigoDePasarela,
  EstadoDelPagoEnLinea,
  PagoEnLinea,
  ResumenDePagosEnLinea,
} from './contrato';

const ESTADOS: Record<EstadoDelPagoEnLinea, { rotulo: string; clase: string }> = {
  INICIADO: { rotulo: 'En curso', clase: 'bg-blue-50 text-blue-700 ring-blue-200' },
  APROBADO: { rotulo: 'Aprobado', clase: 'bg-blue-50 text-blue-700 ring-blue-200' },
  APLICADO: { rotulo: 'Registrado', clase: 'bg-marca-50 text-marca-700 ring-marca-200' },
  RECHAZADO: { rotulo: 'Rechazado', clase: 'bg-armazon-100 text-armazon-500 ring-armazon-200' },
  CADUCADO: { rotulo: 'Abandonado', clase: 'bg-armazon-100 text-armazon-500 ring-armazon-200' },
  DEVUELTO: { rotulo: 'Devuelto', clase: 'bg-amber-50 text-amber-800 ring-amber-200' },
  POR_REVISAR: { rotulo: 'En revisión', clase: 'bg-red-50 text-alerta ring-red-200' },
};

const NOMBRES: Record<CodigoDePasarela, string> = {
  NUVEI: 'Nuvei',
  PLACETOPAY: 'Placetopay',
  SIMULADA: 'Simulada',
};

const dinero = new Intl.NumberFormat('es-EC', { style: 'currency', currency: 'USD' });

function cuantos(p: PagoEnLinea): string {
  const facturas = p.facturas === 1 ? '1 factura' : `${p.facturas} facturas`;
  const titulos = p.titulos === 1 ? '1 título Art. 8' : `${p.titulos} títulos Art. 8`;
  if (p.titulos === 0) {
    return facturas;
  }
  return p.facturas === 0 ? titulos : `${facturas} y ${titulos}`;
}

function fechaYHora(instante: string): string {
  return new Date(instante).toLocaleString('es-EC', {
    timeZone: 'America/Guayaquil',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function PaginaPagosEnLinea() {
  const resumen = useQuery({
    queryKey: ['pagos-en-linea', 'resumen'],
    queryFn: () => api.get<ResumenDePagosEnLinea>('/pagos-en-linea/resumen'),
  });
  const r = resumen.data;

  return (
    <>
      <EncabezadoDePagina
        icono={MonitorSmartphone}
        color="azul"
        titulo="Pagos en línea"
        subtitulo="Lo que pagan los ciudadanos con tarjeta en la ventanilla en línea"
        acciones={
          <Link
            to="/portal/pasarelas"
            className="flex items-center gap-2 rounded-xl border border-armazon-200 bg-white px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-armazon-700 shadow-sm transition-colors hover:border-marca-300 hover:text-marca-700"
          >
            <KeyRound className="size-4" aria-hidden />
            Pasarelas
          </Link>
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <Cifra
          titulo="Recaudado hoy"
          valor={r ? dinero.format(r.recaudadoHoy) : undefined}
          detalle={r ? `${r.aplicadosHoy} pagos` : undefined}
          icono={Wallet}
          tono="marca"
        />
        <Cifra
          titulo="Recaudado en el mes"
          valor={r ? dinero.format(r.recaudadoDelMes) : undefined}
          detalle={r ? `${r.aplicadosDelMes} pagos` : undefined}
          icono={CheckCircle2}
          tono="marca"
        />
        <Cifra titulo="En curso" valor={r?.enCurso} icono={Timer} tono="azul" />
        <Cifra titulo="Por revisar" valor={r?.porRevisar} icono={CircleAlert} tono="alerta" />
        <Cifra
          titulo="Devueltos en el mes"
          valor={r?.devueltosDelMes}
          icono={RotateCcw}
          tono="ambar"
        />
        <Cifra titulo="Rechazados en el mes" valor={r?.rechazadosDelMes} icono={TriangleAlert} />
      </div>

      <Pagos />
    </>
  );
}

function Pagos() {
  const { puede } = useSesion();
  const [buscar, setBuscar] = useState('');
  const [estado, setEstado] = useState<'' | EstadoDelPagoEnLinea>('');
  const [pasarela, setPasarela] = useState<'' | CodigoDePasarela>('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [pagina, setPagina] = useState(0);
  const [tamanio, setTamanio] = useState(25);
  const [detalle, setDetalle] = useState<PagoEnLinea | null>(null);
  const [revisando, setRevisando] = useState<PagoEnLinea | null>(null);
  const texto = useValorDiferido(buscar.trim());

  const pagos = useQuery({
    queryKey: ['pagos-en-linea', 'lista', desde, hasta, estado, pasarela, texto, pagina, tamanio],
    queryFn: () => {
      const p = new URLSearchParams();
      if (desde) p.set('desde', desde);
      if (hasta) p.set('hasta', hasta);
      if (estado) p.set('estado', estado);
      if (pasarela) p.set('pasarela', pasarela);
      if (texto) p.set('q', texto);
      p.set('pagina', String(pagina));
      p.set('tamanio', String(tamanio));
      return api.get<Pagina<PagoEnLinea>>(`/pagos-en-linea?${p.toString()}`);
    },
  });

  function filtrar(accion: () => void) {
    accion();
    setPagina(0);
  }

  const claseFiltro =
    'h-10 rounded-lg border border-armazon-200 bg-white px-2 text-xs text-armazon-700 outline-none focus:border-marca-400';

  const columnas: Columna<PagoEnLinea>[] = [
    {
      clave: 'iniciado',
      encabezado: 'Iniciado',
      clase: 'w-40',
      contenido: (p) => (
        <span className="block text-xs text-armazon-600">
          <span className="block">{fechaYHora(p.iniciadoEn)}</span>
          <span className="importe block text-[11px] text-armazon-400">{p.referencia}</span>
        </span>
      ),
    },
    {
      clave: 'contribuyente',
      encabezado: 'Contribuyente',
      contenido: (p) => (
        <span className="block min-w-0">
          <span className="block truncate text-xs font-semibold uppercase text-armazon-800">
            {p.contribuyente ?? 'Sin nombre en el catastro'}
          </span>
          <span className="importe block text-[11px] text-armazon-500">{p.identificacion}</span>
        </span>
      ),
    },
    {
      clave: 'pasarela',
      encabezado: 'Pasarela',
      clase: 'w-36',
      contenido: (p) => (
        <span className="block text-xs">
          <span className="block font-semibold uppercase text-armazon-700">
            {NOMBRES[p.pasarela]}
          </span>
          <span className="importe block truncate text-[11px] text-armazon-400">
            {p.transaccion ?? '—'}
          </span>
        </span>
      ),
    },
    {
      clave: 'total',
      encabezado: 'Importe',
      alinear: 'derecha',
      clase: 'w-32',
      contenido: (p) => (
        <span className="block">
          <span className="importe block text-sm font-semibold text-armazon-900">
            {dinero.format(p.total)}
          </span>
          <span className="block text-[11px] uppercase text-armazon-400">{cuantos(p)}</span>
        </span>
      ),
    },
    {
      clave: 'estado',
      encabezado: 'Estado',
      alinear: 'centro',
      clase: 'w-32',
      contenido: (p) => (
        <span className="flex flex-col items-center gap-0.5">
          <span
            className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ring-1 ${ESTADOS[p.estado].clase}`}
          >
            {ESTADOS[p.estado].rotulo}
          </span>
          {p.estado === 'POR_REVISAR' && p.revisadoEn && (
            <span className="text-[10px] font-semibold uppercase text-marca-700">Revisado</span>
          )}
        </span>
      ),
    },
    {
      clave: 'acciones',
      encabezado: 'Acciones',
      alinear: 'centro',
      clase: 'w-28 pr-5',
      contenido: (p) => (
        <div className="flex justify-center gap-1">
          <BotonDeFila
            icono={History}
            etiqueta={`Detalle de ${p.referencia}`}
            alPulsar={() => setDetalle(p)}
          />
          {puede('pagos_en_linea.gestionar') && p.estado === 'POR_REVISAR' && !p.revisadoEn && (
            <BotonDeFila
              icono={ClipboardCheck}
              etiqueta={`Revisar ${p.referencia}`}
              alPulsar={() => setRevisando(p)}
            />
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <Tabla
        datos={pagos.data}
        cargando={pagos.isPending}
        columnas={columnas}
        tamanio={tamanio}
        alCambiarPagina={setPagina}
        alCambiarTamanio={(n) => filtrar(() => setTamanio(n))}
        iconoVacio={CreditCard}
        mensajeVacio="NINGÚN PAGO EN LÍNEA COINCIDE CON EL FILTRO"
        entidad="pagos"
        herramientas={
          <div className="flex w-full flex-wrap items-center gap-2">
            <CampoDeBusqueda
              valor={buscar}
              alCambiar={(v) => filtrar(() => setBuscar(v))}
              marcador="Referencia, cédula, nombre o transacción…"
              className="w-full max-w-xs"
            />
            <label className="flex items-center gap-1 text-[11px] uppercase text-armazon-500">
              Desde
              <input
                type="date"
                value={desde}
                onChange={(e) => filtrar(() => setDesde(e.target.value))}
                className={claseFiltro}
              />
            </label>
            <label className="flex items-center gap-1 text-[11px] uppercase text-armazon-500">
              Hasta
              <input
                type="date"
                value={hasta}
                onChange={(e) => filtrar(() => setHasta(e.target.value))}
                className={claseFiltro}
              />
            </label>
            <select
              value={estado}
              onChange={(e) =>
                filtrar(() => setEstado(e.target.value as '' | EstadoDelPagoEnLinea))
              }
              aria-label="Estado"
              className={`${claseFiltro} uppercase`}
            >
              <option value="">Todos los estados</option>
              {(Object.keys(ESTADOS) as EstadoDelPagoEnLinea[]).map((e) => (
                <option key={e} value={e}>
                  {ESTADOS[e].rotulo}
                </option>
              ))}
            </select>
            <select
              value={pasarela}
              onChange={(e) => filtrar(() => setPasarela(e.target.value as '' | CodigoDePasarela))}
              aria-label="Pasarela"
              className={`${claseFiltro} uppercase`}
            >
              <option value="">Todas las pasarelas</option>
              {(Object.keys(NOMBRES) as CodigoDePasarela[]).map((c) => (
                <option key={c} value={c}>
                  {NOMBRES[c]}
                </option>
              ))}
            </select>
          </div>
        }
      />

      {detalle && <DialogoDeDetalle pago={detalle} alCerrar={() => setDetalle(null)} />}
      {revisando && <DialogoDeRevision pago={revisando} alCerrar={() => setRevisando(null)} />}
    </>
  );
}

const TONOS = {
  neutro: 'bg-armazon-100 text-armazon-600',
  marca: 'bg-marca-50 text-marca-700',
  alerta: 'bg-red-50 text-alerta',
  ambar: 'bg-amber-50 text-amber-700',
  azul: 'bg-blue-50 text-blue-700',
} as const;

function Cifra({
  titulo,
  valor,
  detalle,
  icono: Icono,
  tono = 'neutro',
}: {
  titulo: string;
  valor: number | string | undefined;
  detalle?: string | undefined;
  icono: LucideIcon;
  tono?: keyof typeof TONOS;
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-armazon-200 bg-white p-3 shadow-sm">
      <span className={`grid size-9 shrink-0 place-items-center rounded-lg ${TONOS[tono]}`}>
        <Icono className="size-4" aria-hidden />
      </span>
      <div className="min-w-0">
        <p className="importe truncate text-lg font-bold leading-tight text-armazon-900">
          {valor ?? '—'}
        </p>
        <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-armazon-400">
          {titulo}
          {detalle ? ` · ${detalle}` : ''}
        </p>
      </div>
    </div>
  );
}

function BotonDeFila({
  icono: Icono,
  etiqueta,
  alPulsar,
}: {
  icono: LucideIcon;
  etiqueta: string;
  alPulsar: () => void;
}) {
  return (
    <button
      type="button"
      onClick={alPulsar}
      title={etiqueta}
      aria-label={etiqueta}
      className="rounded-lg p-1.5 text-armazon-400 transition-colors hover:bg-armazon-100 hover:text-armazon-700"
    >
      <Icono className="size-[15px]" aria-hidden />
    </button>
  );
}

function DialogoDeDetalle({ pago: p, alCerrar }: { pago: PagoEnLinea; alCerrar: () => void }) {
  return (
    <Modal
      abierto
      alCerrar={alCerrar}
      titulo={p.referencia}
      subtitulo={p.contribuyente ?? p.identificacion}
      icono={History}
      ancho="medio"
    >
      <dl className="grid gap-x-6 gap-y-3 text-xs sm:grid-cols-2">
        <Dato nombre="Identificación" valor={p.identificacion} importe />
        <Dato nombre="Estado" valor={ESTADOS[p.estado].rotulo} />
        <Dato nombre="Importe" valor={`${dinero.format(p.total)} · ${cuantos(p)}`} />
        <Dato nombre="Pasarela" valor={NOMBRES[p.pasarela]} />
        <Dato nombre="Transacción" valor={p.transaccion ?? '—'} importe />
        <Dato nombre="Autorización" valor={p.autorizacion ?? '—'} importe />
        <Dato nombre="Iniciado" valor={fechaYHora(p.iniciadoEn)} />
        <Dato nombre="Aprobado" valor={p.aprobadoEn ? fechaYHora(p.aprobadoEn) : '—'} />
        <Dato nombre="Registrado" valor={p.aplicadoEn ? fechaYHora(p.aplicadoEn) : '—'} />
        <Dato nombre="Devuelto" valor={p.devueltoEn ? fechaYHora(p.devueltoEn) : '—'} />
      </dl>
      {p.detalle && (
        <p className="mt-4 rounded-lg bg-armazon-50 p-3 text-xs uppercase text-armazon-700">
          {p.detalle}
        </p>
      )}
      {p.revisadoEn && (
        <div className="mt-4 rounded-lg border border-marca-200 bg-marca-50 p-3 text-xs uppercase text-marca-900">
          <p className="font-semibold">
            Revisado el {fechaYHora(p.revisadoEn)} por {p.revisadoPor}
          </p>
          <p className="mt-1">{p.notaDeRevision}</p>
        </div>
      )}
    </Modal>
  );
}

function DialogoDeRevision({ pago, alCerrar }: { pago: PagoEnLinea; alCerrar: () => void }) {
  const cliente = useQueryClient();
  const [nota, setNota] = useState('');
  const revisar = useMutation({
    mutationFn: () =>
      api.post<PagoEnLinea>(`/pagos-en-linea/${pago.referencia}/revision`, { nota: nota.trim() }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['pagos-en-linea'] });
      notificar.exito(`Pago ${pago.referencia} revisado`);
      alCerrar();
    },
  });
  const error = revisar.error instanceof ErrorDeApi ? revisar.error.message : null;

  return (
    <Modal
      abierto
      alCerrar={alCerrar}
      titulo="Revisar pago"
      icono={ClipboardCheck}
      ancho="estrecho"
    >
      <div className="space-y-3">
        <p className="rounded-lg bg-armazon-50 px-3 py-2 text-sm font-semibold uppercase text-armazon-900">
          {pago.referencia} · {dinero.format(pago.total)}
        </p>
        <p className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs uppercase text-amber-900">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
          La pasarela cobró y no se pudo registrar ni devolver. Devuelva el dinero en la pasarela o
          registre el cobro en la caja, y escriba aquí qué se hizo.
        </p>
        {error && (
          <p
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm uppercase text-red-900"
          >
            {error}
          </p>
        )}
        <Campo
          etiqueta="Qué se hizo"
          requerido
          ayuda="AL MENOS 10 CARACTERES. QUEDA EN EL HISTORIAL."
        >
          <textarea
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            rows={3}
            maxLength={500}
            className={`${claseEntrada} uppercase`}
          />
        </Campo>
      </div>
      <div className="mt-6 flex justify-end gap-2 border-t border-armazon-100 pt-5">
        <button
          type="button"
          onClick={alCerrar}
          className="rounded-lg px-4 py-2 text-sm font-semibold uppercase text-armazon-600 hover:bg-armazon-100"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={() => revisar.mutate()}
          disabled={revisar.isPending || nota.trim().length < 10}
          className="flex items-center gap-2 rounded-lg bg-marca-600 px-4 py-2 text-sm font-bold uppercase text-white hover:bg-marca-700 disabled:opacity-50"
        >
          <ClipboardCheck className="size-4" aria-hidden />
          {revisar.isPending ? 'Guardando…' : 'Marcar revisado'}
        </button>
      </div>
    </Modal>
  );
}

function Dato({
  nombre,
  valor,
  importe = false,
}: {
  nombre: string;
  valor: string;
  importe?: boolean;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] font-bold uppercase tracking-wider text-armazon-400">{nombre}</dt>
      <dd
        className={`truncate text-armazon-700 ${importe ? 'importe' : 'uppercase'}`}
        title={valor}
      >
        {valor}
      </dd>
    </div>
  );
}
